import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { EmptyPage } from '../EmptyPage';
import { Input } from '../../components/Input';
import { PhoneInput } from '../../components/Input/PhoneInput';
import { Button } from '../../components/Button';
import { createPermsObject, flattenPermissions, mergePermissions } from '../../helpers';
import { EnumsSelector } from './EnumsSelector';
import { StaticPopup } from '../../components/StaticPopup';
import { deleteImage } from '../../redux/staticSlice';
import { deleteUser, editUser } from '../../redux/userSlice';
import { Select } from '../../components/Select';

function comparePermissions(curPerms, newPerms) {
    // Функция для рекурсивного сравнения двух объектов
    function compareRecursive(current, updated) {
        const result = {};

        for (const key in updated) {
            if (current.hasOwnProperty(key)) {
                if (typeof updated[key] === 'object' && !Array.isArray(updated[key])) {
                     //Рекурсивный вызов для вложенных объектов
                    result[key] = compareRecursive(current[key], updated[key]);
                } else if (Array.isArray(updated[key])) {
                    // Сравниваем массивы
                    const currentArray = current[key] || [];
                    const updatedArray = updated[key];

                    // Вычисляем различия
                    const addedItems = updatedArray.filter((item) => !currentArray.includes(item));
                    const removedItems = currentArray.filter((item) => !updatedArray.includes(item));
                    
                    result[key] = updatedArray;
                    if (addedItems.length || removedItems.length) {
                        result.isArrayChanged = true;
                        result.pushed = addedItems;
                        result.deleted = removedItems;
                    }
                } else {
                     //Копируем значение флага
                    result[key] = updated[key];
                }
            } else {
                // Ключ отсутствует в текущих правах, просто копируем обновленный объект
                result[key] = updated[key];
            }

            // Проверяем изменение флага
            if (key === 'flag') {
                const isFlagChanged = current[key] !== updated[key];
                if (isFlagChanged) {
                    result.isFlagChanged = true;
                }
            }
        }

        // Проверяем флаги на верхнем уровне
        if (updated.hasOwnProperty('flag') && current.hasOwnProperty('flag') && current.flag !== updated.flag) {
            result.isFlagChanged = true;
        }

        return result;
    }

    // Начинаем рекурсивное сравнение
    return compareRecursive(curPerms, newPerms);
}

function renderPermissions(
    template,
    permissions,
    setPermissions,
    setPermsList,
    thisUserPerms,
    editAccess,
    parentFlag = false,
) {
    return Object.entries(template).map(([key, value]) => {
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            // Если текущее значение является объектом, рекурсивно рендерим его
            const [popup, setPopup] = useState(false);
            const setEnumsHandler = (id) => {
                const listName = permissions[key].enums.name;
                if (permissions[key][listName].some((eId) => eId === id)) {
                    //Добавляем в список
                    const newList = permissions[key][listName].filter((eId) => eId !== id);
                    setPermsList(key, newList);
                    return;
                }
                const newList = [...permissions[key][listName], id];
                setPermsList(key, newList);
            };
            const setPermsHandler = (e) => {
                const { checked } = e.target;
                setPermissions(key, checked);
            };

            const level = permissions[key].level;
            const { enums } = permissions[key];
            const listName = enums?.name;
            const colorClassName = permissions[key].flag
                ? 'green'
                : permissions[key][listName]?.length
                ? 'yellow'
                : '';
            const hasAllAccess = thisUserPerms[key].flag;
            const hasSomeAccess = hasAllAccess || !!enums?.list?.length;
            const isSelectDisabled = permissions[key].flag || !parentFlag;
            return level > 1 ? (
                <React.Fragment key={key}>
                    {hasSomeAccess && (
                        <div className="access__item" style={{ marginLeft: `${level * 16}px` }}>
                            {popup && (
                                <EnumsSelector
                                    selectedEnums={permissions[key][listName]}
                                    selectEnums={(id) => setEnumsHandler(id)}
                                    enums={enums.list}
                                    closeFn={() => setPopup(false)}
                                    title={enums.title}
                                />
                            )}
                            <input
                                type="checkbox" 
                                className={`${!hasAllAccess ? 'disabled' : ''}`}
                                checked={permissions[key].flag}
                                disabled={!hasAllAccess}
                                readOnly={!editAccess}
                                title={value.description}
                                id={key}
                                onChange={editAccess ? setPermsHandler : null}
                            />
                            <label
                                className={`${colorClassName} ${!hasAllAccess ? 'disabled' : ''}`}
                                htmlFor={key}
                                title={value.description}>
                                {value.label}
                            </label>
                            {enums && (
                                <div className="button-container">
                                    <span
                                        className={`select-button ${
                                            isSelectDisabled ? 'disabled' : ''
                                        }`}
                                        onClick={
                                            isSelectDisabled || !editAccess
                                                ? null
                                                : () => setPopup(true)
                                        }>
                                        ...или только выбранных
                                    </span>
                                    {!!permissions[key][listName]?.length && (
                                        <span style={{ marginLeft: '6px', color: 'yellow' }}>
                                            Выбрано: {permissions[key][listName].length}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    {renderPermissions(
                        value,
                        permissions,
                        setPermissions,
                        setPermsList,
                        thisUserPerms,
                        editAccess,
                        permissions[key].flag,
                    )}
                </React.Fragment>
            ) : (
                <React.Fragment key={key}>
                    <div className={`access__category ${!level ? 'full-width' : ''}`} key={key}>
                        <div className="access__item" style={{ marginLeft: `${level * 16}px` }}>
                            {popup && (
                                <EnumsSelector
                                    selectedEnums={permissions[key][listName]}
                                    selectEnums={(id) => setEnumsHandler(id)}
                                    enums={enums.list}
                                    closeFn={() => setPopup(false)}
                                    title={enums.title}
                                />
                            )}
                            <input
                                type="checkbox" 
                                checked={permissions[key].flag}
                                title={value.description}
                                readOnly={!editAccess}
                                id={key}
                                onChange={editAccess ? setPermsHandler : null}
                            />
                            <label
                                className={colorClassName}
                                htmlFor={key}
                                title={value.description}>
                                {value.label}
                            </label>
                            {enums && (
                                <div className="button-container">
                                    <span
                                        className={`select-button ${
                                            isSelectDisabled ? 'disabled' : ''
                                        }`}
                                        onClick={
                                            isSelectDisabled || !editAccess
                                                ? null
                                                : () => setPopup(true)
                                        }>
                                        ...или только выбранных
                                    </span>
                                    {!!permissions[key][listName]?.length && (
                                        <span style={{ marginLeft: '6px', color: 'yellow' }}>
                                            выбрано: {permissions[key][listName].length}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                        {!!level &&
                            renderPermissions(
                                value,
                                permissions,
                                setPermissions,
                                setPermsList,
                                thisUserPerms,
                                editAccess,
                                permissions[key].flag,
                            )}
                    </div>
                    {!level &&
                        renderPermissions(
                            value,
                            permissions,
                            setPermissions,
                            setPermsList,
                            thisUserPerms,
                            editAccess,
                            permissions[key].flag,
                        )}
                </React.Fragment>
            );
        } else {
            return null;
        }
    });
}

const UserFieldset = ({
    fieldName,
    updated,
    value,
    editable,
    setNewVal,
    isPhone = false,
}) => {
    const [edit, setEdit] = useState(false);
    const [localVal, setLocalVal] = useState(value);

    const saveHandler = () => {
        if (!isPhone) {
            setNewVal && setNewVal(localVal);
        } else {
            setNewVal(localVal || null);
        }
        setEdit(false);
    };
    const cancelHandler = () => {
        setLocalVal(value);
        setEdit(false);
    };
    return (
        <div className="user-fieldset" onDoubleClick={() => editable && setEdit(true)}>
            <div className="user-fieldset__label">{fieldName}</div>
            {!edit && (
                <div className="user-fieldset__value">
                    <span className={`value ${updated ? 'updated' : ''}`}>{value}</span>
                    {editable && (
                        <span className="user-fieldset__edit" onClick={() => setEdit(true)}>
                            Изменить
                        </span>
                    )}
                </div>
            )}
            {edit ? (
                !isPhone ? (
                    <Input value={localVal} changeHandler={(e) => setLocalVal(e.target.value)} />
                ) : (
                    <PhoneInput
                        value={localVal || ''}
                        changeHandler={(e) => setLocalVal(e.target.value)}
                    />
                )
            ) : null}
            {edit && localVal !== value && (
                <span className="cancel" onClick={saveHandler}>
                    Сохранить
                </span>
            )}
            {edit && (
                <span className="cancel" onClick={cancelHandler}>
                    Отмена
                </span>
            )}
        </div>
    );
};

const hasFullAccess = (permissions) => {
    for (const key in permissions) {
        if (!permissions[key].flag) {
            return false;
        }
    }
    return true;
};

export const EditUser = ({}) => {
    const { pathname } = useLocation();
    const userId = pathname.split('/')[pathname.split('/').length - 1];
    const userInfo = useSelector((state) => state.auth);

    const cities = useSelector((state) => state.settings.userCities);
    const cityList = cities.length ? cities : [{ _id: null, name: 'Нет доступных городов' }]; 

    const userList = useSelector((state) => state.users.list);
    const thisUser = userList.find((u) => u._id === userId);

    if (!thisUser) {
        return <EmptyPage message='Данный пользователь не существует либо у Вас нет к нему доступа'/>
    }
    const settings = useSelector((state) => state.settings);
    const users = useSelector((state) => state.users.list);
    const creatorInfo = thisUser.details?.created.by;

    const dispatch = useDispatch();
    const [files, setFiles] = useState([]);
    const [city, setCity] = useState(cityList[0]._id);
    const fileInputRef = useRef(null);

    const setFilesHandler = (e) => {
        const selectedFiles = Array.from(e.target.files);
        setFiles(selectedFiles);
    };
    const clickInputHandler = () => {
        fileInputRef.current.click();
    };

    const profiles = useSelector((state) => state.settings.userProfiles);
    const [isEditProfile, setEditProfile] = useState(false);
    const [selectedProfiles, selectProfile] = useState(thisUser.profiles.map(p => p._id));

    const isProfilesChanged =
        [...thisUser.profiles.map((p) => p._id)].sort((a, b) => a.localeCompare(b)).join('') !==
        [...selectedProfiles].sort((a, b) => a.localeCompare(b)).join('');

    const selectProfileHandler = (profId) => {
        if (selectedProfiles.some((pId) => pId === profId)) {
            selectProfile(selectedProfiles.filter((pId) => pId !== profId));
            return;
        }
        selectProfile([...selectedProfiles, profId]);
    };
    const cancelEditProfileHandler = () => {
      selectProfile(thisUser.profiles.map(p => p._id));
      setEditProfile(false);
    }

    const setFullAccess = () => {
        const newPerms = { ...permissions };
        for (const key in newPerms) {
            newPerms[key].flag = true;
            if (newPerms[key].enums) {
                newPerms[key][newPerms[key].enums.name] = [];
            }
        }
        setPermissions(newPerms);
    };

    const withEnums = (perms) => {
        perms.ALL_CITY_ORDERS.enums = {
            list: settings.orderCities,
            name: 'cities',
            title: 'города',
        };
        perms.ALL_PROFILE_ORDERS.enums = {
            list: settings.orderProfiles,
            name: 'profiles',
            title: 'профили',
        };
        perms.ALL_USER_ORDERS.enums = {
            list: settings.orderUsers
                .map((uId) => ({
                    _id: uId,
                    name: users.find((u) => u._id === uId)?.fullname,
                }))
                .filter((u) => u),
            name: 'users',
            title: 'пользователей',
        };

        perms.ALL_CITY_OFFICE.enums = {
            list: settings.officeCities,
            name: 'cities',
            title: 'города',
        };

        perms.ALL_USERS.enums = {
            list: settings.availableUsers,
            name: 'users',
            title: 'пользователи',
        };
        perms.ALL_PROFILES.enums = {
            list: settings.userProfiles,
            name: 'profiles',
            title: 'профили',
        };
        perms.ALL_CITIES.enums = { list: settings.userCities, name: 'cities', title: 'города' };
        perms.ALL_CITY_ADVERT.enums = { list: settings.advertCities, name: 'cities', title: 'города' };
        return perms;
    };

    
    const permissionTemplate = useSelector((state) => state.settings.permissionSchema);
    const [permissions, setPermissions] = useState(withEnums(
        flattenPermissions(mergePermissions(permissionTemplate, thisUser.role.permissions)),
    ));
    const thisUserPerms = flattenPermissions(userInfo.role.permissions);
    const isPermsChanged = JSON.stringify(permissions) !== JSON.stringify(withEnums(
        flattenPermissions(mergePermissions(permissionTemplate, thisUser.role.permissions)),
    ));

    const setPermsHandler = (key, flag) => {
        
        const { path, level } = permissions[key]; 
        const changedState = { ...permissions }; 
        if (flag) {
            
            path.forEach((k) => (changedState[k] = { ...permissions[k], flag }));
            if (changedState[key].enums) {
                changedState[key][changedState[key].enums.name] = [];
            }
        } else {
            
            changedState[key] = { ...permissions[key], flag }; 
            for (const k in permissions) {
                
                if (permissions[k].path[level] === key) {
                    
                    changedState[k] = { ...permissions[k], flag }; //Ставим false дочерним
                } 
                const enumList = changedState[k][changedState[k].enums?.name];
                if (enumList) {
                     //Если у текущего права есть список
                    changedState[k].path.forEach((k2, index, arr) => {
                          //Тогда проверим все родительские на false
                        if (!changedState[k2].flag && index !== arr.length - 1) {
                            //Кроме последнего пути (текущий уровень)
                            changedState[k][changedState[k].enums.name] = [];
                            return;
                        }
                    });
                }
            }
        }
        setPermissions({ ...permissions, ...changedState });
    };

    const setPermsListHandler = (key, list) => {
        setPermissions({
            
            ...permissions,
            [key]: {
                
                ...permissions[key],
                flag: false, 
                [permissions[key].enums.name]: list,
            },
        });
    };
    const [docsPopup, setDocsPopup] = useState(false);

    const [fullname, setFullname] = useState(thisUser.fullname);
    const phoneNumber =
        (thisUser.personalData?.phoneNumber &&
            thisUser.personalData?.phoneNumber !== 'Не указан') ||
        null;
    const [phone, setPhone] = useState(phoneNumber);
    const [privilegeLevel, setPrivilegeLevel] = useState(thisUser.role.privilegeLevel);
    const [roleName, setRoleName] = useState(thisUser.role.name);
    const editAccess = thisUser.interact.edit;

    const isChanged = (() => {
        return (
            thisUser.fullname !== fullname ||
            phoneNumber !== phone ||
            thisUser.city._id !== city ||
            files.length ||
            isProfilesChanged ||
            isPermsChanged ||
            thisUser.role.name !== roleName ||
            thisUser.role.privilegeLevel !== privilegeLevel
        );
    })();

    const deleteImageHandler = (type, fileName) => {
        dispatch(deleteImage({ type, name: fileName }));
    };
    const deleteUserHandler = () => {
        if (window.confirm('Вы собираетесь удалить пользователя. Подтвердите действие')) {
            dispatch(deleteUser({userId: thisUser._id, deleted: true}));
        }
    };
    const recoveryHandler = () => {
        dispatch(deleteUser({userId: thisUser._id, deleted: false}));
    }
    const resetHandler = () => {
        setFiles([]);
    }
    const editUserHandler = () => {
        const changesObj = {
            _id: thisUser._id
        };
        if (thisUser.fullname !== fullname) {
            changesObj.fullname = fullname;
        }
        if (phoneNumber !== phone) {
            changesObj.phoneNumber = phone;
        }
        changesObj.documents = files;
        if (thisUser.city._id !== city) {
            changesObj.city = city;
        }
        if (isProfilesChanged) {
            changesObj.profiles = selectedProfiles;
        }

        if (thisUser.role.name !== roleName) {
            changesObj.roleName = roleName;
        }
        if (thisUser.role.privilegeLevel !== privilegeLevel) {
            changesObj.privilegeLevel = +privilegeLevel;
        }
        if (isPermsChanged) {
            changesObj.permissions = createPermsObject(permissionTemplate, permissions);
        }
        dispatch(editUser({userData: changesObj, resetFunc: resetHandler}));

    }
    if (!thisUser) {
        return <EmptyPage message={'Неизвестный пользователь'} />;
    }
    return (
        <div className="edit-user-page edit-user page">
            {docsPopup && (
                <StaticPopup
                    deleteAccess={editAccess && !!thisUser.personalData}
                    deleteFunc={deleteImageHandler}
                    closeFn={() => setDocsPopup(false)}
                    content={thisUser.personalData.documents.map(n => ({fileName: n, isPhoto: true, type: 'userDoc'}))}
                />
            )}
            <h2 className="edit-user__title">
                {userInfo.id === thisUser._id
                    ? 'Редактирование профиля'
                    : `Пользователь ${thisUser.fullname}`}
            </h2>
            <h3 className='edit-user__subtitle'>
            {creatorInfo ? `(Создан ${creatorInfo._id === userInfo.id ? 'вами' : `пользователем '${creatorInfo.fullname}'`})` : ''}
            </h3>
            <div className="edit-user__body">
                <fieldset className="general dark-theme-bg0">
                    <legend>Основное</legend>
                    <UserFieldset
                        updated={false}
                        editable={false}
                        fieldName="Логин"
                        value={thisUser.telegramId}
                    />
                    <UserFieldset
                        updated={false}
                        editable={false}
                        fieldName="Пароль"
                        value={thisUser.password || 'Скрыт'}
                    />
                    <UserFieldset
                        updated={thisUser.fullname !== fullname}
                        value={fullname}
                        setNewVal={setFullname}
                        editable={editAccess}
                        fieldName="Полное имя"
                    />
                </fieldset>
                <fieldset className="personal dark-theme-bg0">
                    <legend>Персональные данные</legend>
                    <UserFieldset
                        updated={phoneNumber !== phone}
                        isPhone={true}
                        setNewVal={setPhone}
                        editable={!!thisUser.personalData.phoneNumber && editAccess}
                        fieldName="Телефон"
                        value={phone || 'Не указан'}
                    />
                    <Select
                        label="Город"
                        disabled={!editAccess}
                        enums={cityList}
                        value={city}
                        changeHandler={(e) => setCity(e.target.value)}
                    />
                    <div className="user-fieldset">
                        <div className="user-fieldset__label">Документы</div>
                        <div className="user-fieldset__value">
                            {thisUser.personalData.documents.length ? (
                                <span onClick={() => setDocsPopup(true)}>Показать</span>
                            ) : (
                                'Нет документов'
                            )}
                            {editAccess && (
                                <span onClick={clickInputHandler}>
                                    {files.length ? `Выбрано ${files.length} фото` : 'Добавить'}
                                </span>
                            )}
                            <input
                                accept=".jpg,.jpeg,.png"
                                style={{ display: 'none' }}
                                ref={fileInputRef}
                                type="file"
                                multiple={true}
                                onChange={setFilesHandler}
                            />
                            {!!files.length && <span onClick={() => setFiles([])}>Сброс</span>}
                        </div>
                    </div>
                </fieldset>
                {!isEditProfile && (
                    <fieldset className='dark-theme-bg0'>
                        <legend>Профили</legend>
                        <div
                            className="user-fieldset"
                            onDoubleClick={() => editAccess && setEditProfile(true)}>
                            <div className="user-fieldset__label">Рабочие профили</div>
                            <div className="user-fieldset__value">
                                <span className={`value ${isProfilesChanged ? 'updated' : ''}`}>
                                    {profiles.filter(p => selectedProfiles.some(p2=>p2===p._id)).map(p=>p.name).join(', ') || 'Нет рабочих профилей'}
                                </span>
                                {editAccess && (
                                    <span
                                        className="user-fieldset__edit"
                                        onClick={() => setEditProfile(true)}>
                                        Изменить
                                    </span>
                                )}
                            </div>
                        </div>
                    </fieldset>
                )}
                {isEditProfile && (
                    <fieldset className="profiles dark-theme-bg0">
                        <legend>Профили</legend>
                        <p>Укажите, по каким профилям будет работать пользователь:</p>
                        <div>
                            {profiles.map((p) => (
                                <Button
                                    key={p._id}
                                    classNames={
                                        selectedProfiles.some((pId) => pId === p._id)
                                            ? 'active'
                                            : ''
                                    }
                                    text={p.name}
                                    onClick={() => selectProfileHandler(p._id)}
                                />
                            ))}
                            {!profiles.length && 'Нет доступных профилей'}
                        </div>
                       <div style={{textAlign: 'center'}}>
                        <span className='cancel' onClick={() => setEditProfile(false)}>Сохранить</span>
                        <span className='cancel' onClick={cancelEditProfileHandler}>Отмена</span>
                       </div>
                    </fieldset>
                )}
                <fieldset className="access dark-theme-bg0">
                    <legend>Доступ</legend>
                    {hasFullAccess(thisUserPerms) && (
                        <Button text="выдать полный доступ" onClick={setFullAccess} />
                    )}
                    <div className="access__list">
                        {renderPermissions(
                            permissionTemplate,
                            permissions,
                            setPermsHandler,
                            setPermsListHandler,
                            thisUserPerms,
                            editAccess,
                        )}
                    </div>
                    <UserFieldset
                        updated={thisUser.role.name !== roleName}
                        setNewVal={setRoleName}
                        editable={editAccess}
                        fieldName="Роль"
                        value={roleName}
                    />
                    <UserFieldset
                        updated={thisUser.role.privilegeLevel !== privilegeLevel}
                        setNewVal={setPrivilegeLevel}
                        editable={editAccess}
                        fieldName='Уровень привилегий'
                        value={privilegeLevel}
                    />
                </fieldset>
            </div>
            <div className="edit-user__footer">
                <Button
                    text={isChanged ? 'Сохранить изменения' : 'Нет изменений для сохранения'}
                    disabled={!isChanged}
                    onClick={editUserHandler}
                />
                {userInfo.id !== thisUser._id && !thisUser.deleted && (
                    <Button
                        text="Удалить пользователя"
                        disabled={!editAccess}
                        onClick={deleteUserHandler}
                    />
                )}
                {userInfo.id !== thisUser._id && thisUser.deleted && (
                    <Button
                        text="Восстановить пользователя"
                        disabled={!editAccess}
                        onClick={recoveryHandler}
                    />
                )}
            </div>
        </div>
    );
};
